import { CreateCarRequest, ICarModel } from "./CarModel";

// Form field names to model property mapping
export const CAR_FORM_FIELD_MAP = {
  // Definition tab fields
  'plate': 'carDefinition.plate',
  'branch': 'carDefinition.branch', 
  'brand': 'carDefinition.brand',
  'location': 'carDefinition.location',
  'type': 'carDefinition.modelType',
  'carStatus': 'carDefinition.carStatus',
  'vehicleGroup': 'carDefinition.vehicleGroup',
  'passiveReason': 'carDefinition.deactivationReason',
  'transmission': 'carDefinition.transmission',
  'color': 'carDefinition.color',
  'fuelType': 'carDefinition.fuelType',
  'lastKm': 'carDefinition.lastKm',
  'model': 'carDefinition.modelYear',
  'lastFuel': 'carDefinition.lastFuel',
  'webReservation': 'carDefinition.webReservationStatus',
  'officeReservation': 'carDefinition.officeReservationStatus',
  
  // Detail tab fields
  'vehicleStatus': 'carDetail.vehicleStatus',
  'ogsHgsAvailable': 'carDetail.hasOgsHgs',
  'bodyType': 'carDetail.bodyType',
  'cleaningStatus': 'carDetail.cleaningStatus',
  'licenseDate': 'carDetail.licenseDate',
  'licenseDocumentNo': 'carDetail.licenseDocumentNo',
  'registrationDate': 'carDetail.registrationDate',
  'chassisNumber': 'carDetail.chassisNumber',
  'engineNumber': 'carDetail.engineNumber',
  'insuranceFollow': 'carDetail.insuranceFollow',
  'ogsHgsNumber': 'carDetail.ogsHgsNumber',
  'ogsHgsBalance': 'carDetail.ogsHgsBalance',
  'radioCode': 'carDetail.radioCode',
  'enginePower': 'carDetail.enginePower',
  'cylinderVolume': 'carDetail.cylinderVolume',
  'integrationCode': 'carDetail.integrationCode',
  'autoEngineStop': 'carDetail.autoEngineStop',
  'hgsCompany': 'carDetail.hgsCompany',
  'vehicleTrackingNumber': 'carDetail.vehicleTrackingNumber',
  'periodicServiceTracking': 'carDetail.periodicServiceTracking',
  'detail': 'carDetail.detail',

  // Insurance tab fields
  'trafficInsuranceStartDate': 'carInsurance.trafficInsuranceStartDate',
  'trafficInsuranceEndDate': 'carInsurance.trafficInsuranceEndDate',
  'insuranceCompany': 'carInsurance.insuranceCompany',
  'insuranceAgency': 'carInsurance.insuranceAgency',
  'trafficPolicyNumber': 'carInsurance.trafficPolicyNumber',
  'inspectionStartDate': 'carInsurance.inspectionStartDate',
  'inspectionEndDate': 'carInsurance.inspectionEndDate',
  'cascoStartDate': 'carInsurance.cascoStartDate',
  'cascoEndDate': 'carInsurance.cascoEndDate',
  'cascoCompany': 'carInsurance.cascoCompany',
  'cascoAgency': 'carInsurance.cascoAgency',
  'cascoPolicyNumber': 'carInsurance.cascoPolicyNumber',
  'cascoCode': 'carInsurance.cascoCode',

  // Warning/Financial tab fields
  'fleetEntryDate': 'carWarning.fleetEntryDate',
  'cascoValue': 'carWarning.cascoValue',
  'purchasingCompany': 'carWarning.purchasingCompany',
  'closeDate': 'carWarning.closeDate',
  'pledgeStatus': 'carWarning.pledgeStatus',
  'vehicleOwner': 'carWarning.vehicleOwner',
  'vehicleOwnerCompany': 'carWarning.vehicleOwnerCompany',
  'vehicleOwnerNumber': 'carWarning.vehicleOwnerNumber',
  'secondaryVehicleOwner': 'carWarning.secondaryVehicleOwner',
  'purchaseTotalPrice': 'carWarning.purchaseTotalPrice',
  'purchasePriceExclTax': 'carWarning.purchasePriceExclTax',
  'purchaseVAT': 'carWarning.purchaseVAT',
  'purchaseSCT': 'carWarning.purchaseSCT',
  'purchaseDate': 'carWarning.purchaseDate',
  'purchaseInvoiceNumber': 'carWarning.purchaseInvoiceNumber',
  'vehicleSecondHandPrice': 'carWarning.vehicleSecondHandPrice',
  'fleetManagementCost': 'carWarning.fleetManagementCost',
  'monthlyCost': 'carWarning.monthlyCost',
  'additionalPrice': 'carWarning.additionalPrice',
  'creditCompany': 'carWarning.creditCompany',
  'externalSupplierKmLimit': 'carWarning.externalSupplierKmLimit',
  'assistServiceProvider': 'carWarning.assistServiceProvider',
  'specialCode1': 'carWarning.specialCode1',
  'specialCode2': 'carWarning.specialCode2',
  'specialCode3': 'carWarning.specialCode3',
  'specialCode4': 'carWarning.specialCode4',
  'specialCode5': 'carWarning.specialCode5',
  'fleetExitDate': 'carWarning.fleetExitDate',
  'plannedExitDate': 'carWarning.plannedExitDate',
  'saleWarningKm': 'carWarning.saleWarningKm',
  'description': 'carWarning.description',
  'tireInfo': 'carWarning.tireInfo',
  'snowTireStatus': 'carWarning.snowTireStatus',
  'spareKeyStatus': 'carWarning.spareKeyStatus',
  'tireWarningKm': 'carWarning.tireWarningKm',
  'lastMaintenanceNumber': 'carWarning.lastMaintenanceNumber',
  'lastMaintenanceKm': 'carWarning.lastMaintenanceKm',
  'lastMaintenanceDate': 'carWarning.lastMaintenanceDate',
  'kmInspectionNumber': 'carWarning.kmInspectionNumber',
  'inspectionType': 'carWarning.inspectionType',
  'kmInspectionDate': 'carWarning.kmInspectionDate',
  'lastPeriodicNumber': 'carWarning.lastPeriodicNumber',
  'lastPeriodicKm': 'carWarning.lastPeriodicKm',
  'lastPeriodicDate': 'carWarning.lastPeriodicDate',
  'lastTireNumber': 'carWarning.lastTireNumber',
  'lastTireKm': 'carWarning.lastTireKm',
  'lastTireDate': 'carWarning.lastTireDate',
} as const;

// Complete initial values with ALL field names for forms
export const initialCarFormFieldValues = {
  // === CAR DEFINITION FIELDS ===
  plate: '',
  branch: '',
  brand: '',
  location: '',
  type: '', // maps to modelType
  carStatus: '1',
  vehicleGroup: '',
  passiveReason: '', // maps to deactivationReason
  transmission: '',
  color: '',
  fuelType: '',
  lastKm: 0,
  model: new Date().getFullYear(), // maps to modelYear
  lastFuel: '',
  webReservation: '', // maps to webReservationStatus
  officeReservation: '', // maps to officeReservationStatus
  
  // === CAR DETAIL FIELDS ===
  vehicleStatus: '1',
  ogsHgsAvailable: '', // maps to hasOgsHgs
  bodyType: '',
  cleaningStatus: '',
  licenseDate: '',
  licenseDocumentNo: '',
  registrationDate: '',
  chassisNumber: '',
  engineNumber: '',
  insuranceFollow: '',
  ogsHgsNumber: '',
  ogsHgsBalance: 0,
  radioCode: '',
  enginePower: '',
  cylinderVolume: 0,
  integrationCode: '',
  autoEngineStop: '',
  hgsCompany: '',
  vehicleTrackingNumber: '',
  periodicServiceTracking: '',
  detail: '',

  // === CAR INSURANCE FIELDS ===
  trafficInsuranceStartDate: '',
  trafficInsuranceEndDate: '',
  insuranceCompany: '',
  insuranceAgency: '',
  trafficPolicyNumber: '',
  inspectionStartDate: '',
  inspectionEndDate: '',
  cascoStartDate: '',
  cascoEndDate: '',
  cascoCompany: '',
  cascoAgency: '',
  cascoPolicyNumber: '',
  cascoCode: '',

  // === CAR WARNING/FINANCIAL FIELDS ===
  fleetEntryDate: '',
  cascoValue: 0,
  purchasingCompany: '',
  closeDate: '',
  pledgeStatus: '',
  vehicleOwner: '',
  vehicleOwnerCompany: '',
  vehicleOwnerNumber: '',
  secondaryVehicleOwner: '',
  purchaseTotalPrice: 0,
  purchasePriceExclTax: 0,
  purchaseVAT: 0,
  purchaseSCT: 0,
  purchaseDate: '',
  purchaseInvoiceNumber: '',
  vehicleSecondHandPrice: 0,
  fleetManagementCost: 0,
  monthlyCost: 0,
  additionalPrice: 0,
  creditCompany: '',
  externalSupplierKmLimit: 0,
  assistServiceProvider: '',
  specialCode1: '',
  specialCode2: '',
  specialCode3: '',
  specialCode4: '',
  specialCode5: '',
  fleetExitDate: '',
  plannedExitDate: '',
  saleWarningKm: 0,
  description: '',
  tireInfo: '',
  snowTireStatus: '',
  spareKeyStatus: '',
  tireWarningKm: 0,
  lastMaintenanceNumber: '',
  lastMaintenanceKm: 0,
  lastMaintenanceDate: undefined,
  kmInspectionNumber: '',
  inspectionType: '',
  kmInspectionDate: undefined,
  lastPeriodicNumber: '',
  lastPeriodicKm: 0,
  lastPeriodicDate: undefined,
  lastTireNumber: '',
  lastTireKm: 0,
  lastTireDate: undefined,
};

// Function to convert form values to API model - COMPLETE VERSION
export const mapFormToCarModel = (formValues: any): CreateCarRequest => {
  return {
    carDefinition: {
      plate: formValues.plate || '',
      brand: formValues.brand || '',
      modelType: formValues.type || '', // form 'type' -> model 'modelType'
      vehicleGroup: formValues.vehicleGroup || '',
      transmission: formValues.transmission || '',
      fuelType: formValues.fuelType || '',
      modelYear: formValues.model || new Date().getFullYear(), // form 'model' -> model 'modelYear'
      webReservationStatus: formValues.webReservation || 'available', // form 'webReservation' -> model 'webReservationStatus'
      branch: formValues.branch || '',
      location: formValues.location || '',
      carStatus: formValues.carStatus || 'active',
      deactivationReason: formValues.passiveReason || '', // form 'passiveReason' -> model 'deactivationReason'
      color: formValues.color || '',
      lastKm: formValues.lastKm || 0,
      lastFuel: formValues.lastFuel || '',
      officeReservationStatus: formValues.officeReservation || 'available', // form 'officeReservation' -> model 'officeReservationStatus'
    },
    carDetail: {
      detail: formValues.detail || '',
      bodyType: formValues.bodyType || '',
      cleaningStatus: formValues.cleaningStatus || '',
      licenseDate: formValues.licenseDate || '',
      licenseDocumentNo: formValues.licenseDocumentNo || '',
      registrationDate: formValues.registrationDate || '',
      chassisNumber: formValues.chassisNumber || '',
      engineNumber: formValues.engineNumber || '',
      insuranceFollow: formValues.insuranceFollow || '',
      hasOgsHgs: formValues.ogsHgsAvailable || 'no', // form 'ogsHgsAvailable' -> model 'hasOgsHgs'
      vehicleStatus: formValues.vehicleStatus || 'active',
      ogsHgsNumber: formValues.ogsHgsNumber || '',
      ogsHgsBalance: formValues.ogsHgsBalance || 0,
      radioCode: formValues.radioCode || '',
      enginePower: formValues.enginePower || '',
      cylinderVolume: formValues.cylinderVolume || 0,
      integrationCode: formValues.integrationCode || '',
      autoEngineStop: formValues.autoEngineStop || 'no',
      hgsCompany: formValues.hgsCompany || '',
      vehicleTrackingNumber: formValues.vehicleTrackingNumber || '',
      periodicServiceTracking: formValues.periodicServiceTracking || 'no',
    },
    carInsurance: {
      trafficInsuranceStartDate: formValues.trafficInsuranceStartDate || '',
      trafficInsuranceEndDate: formValues.trafficInsuranceEndDate || '',
      insuranceCompany: formValues.insuranceCompany || '',
      insuranceAgency: formValues.insuranceAgency || '',
      trafficPolicyNumber: formValues.trafficPolicyNumber || '',
      inspectionStartDate: formValues.inspectionStartDate || '',
      inspectionEndDate: formValues.inspectionEndDate || '',
      cascoStartDate: formValues.cascoStartDate || '',
      cascoEndDate: formValues.cascoEndDate || '',
      cascoCompany: formValues.cascoCompany || '',
      cascoAgency: formValues.cascoAgency || '',
      cascoPolicyNumber: formValues.cascoPolicyNumber || '',
      cascoCode: formValues.cascoCode || '',
    },
    carWarning: {
      fleetEntryDate: formValues.fleetEntryDate || '',
      cascoValue: formValues.cascoValue || 0,
      purchasingCompany: formValues.purchasingCompany || '',
      closeDate: formValues.closeDate || '',
      pledgeStatus: formValues.pledgeStatus || '',
      vehicleOwner: formValues.vehicleOwner || '',
      vehicleOwnerCompany: formValues.vehicleOwnerCompany || '',
      vehicleOwnerNumber: formValues.vehicleOwnerNumber || '',
      secondaryVehicleOwner: formValues.secondaryVehicleOwner || '',
      purchaseTotalPrice: formValues.purchaseTotalPrice || 0,
      purchasePriceExclTax: formValues.purchasePriceExclTax || 0,
      purchaseVAT: formValues.purchaseVAT || 0,
      purchaseSCT: formValues.purchaseSCT || 0,
      purchaseDate: formValues.purchaseDate || '',
      purchaseInvoiceNumber: formValues.purchaseInvoiceNumber || '',
      vehicleSecondHandPrice: formValues.vehicleSecondHandPrice || 0,
      fleetManagementCost: formValues.fleetManagementCost || 0,
      monthlyCost: formValues.monthlyCost || 0,
      additionalPrice: formValues.additionalPrice || 0,
      creditCompany: formValues.creditCompany || '',
      externalSupplierKmLimit: formValues.externalSupplierKmLimit || 0,
      assistServiceProvider: formValues.assistServiceProvider || '',
      specialCode1: formValues.specialCode1 || '',
      specialCode2: formValues.specialCode2 || '',
      specialCode3: formValues.specialCode3 || '',
      specialCode4: formValues.specialCode4 || '',
      specialCode5: formValues.specialCode5 || '',
      fleetExitDate: formValues.fleetExitDate || '',
      plannedExitDate: formValues.plannedExitDate || '',
      saleWarningKm: formValues.saleWarningKm || 0,
      description: formValues.description || '',
      tireInfo: formValues.tireInfo || '',
      snowTireStatus: formValues.snowTireStatus || 'YOK',
      spareKeyStatus: formValues.spareKeyStatus || 'YOK',
      tireWarningKm: formValues.tireWarningKm || 0,
      lastMaintenanceNumber: formValues.lastMaintenanceNumber || '',
      lastMaintenanceKm: formValues.lastMaintenanceKm || 0,
      lastMaintenanceDate: formValues.lastMaintenanceDate || undefined,
      kmInspectionNumber: formValues.kmInspectionNumber || '',
      inspectionType: formValues.inspectionType || '',
      kmInspectionDate: formValues.kmInspectionDate || undefined,
      lastPeriodicNumber: formValues.lastPeriodicNumber || '',
      lastPeriodicKm: formValues.lastPeriodicKm || 0,
      lastPeriodicDate: formValues.lastPeriodicDate || undefined,
      lastTireNumber: formValues.lastTireNumber || '',
      lastTireKm: formValues.lastTireKm || 0,
      lastTireDate: formValues.lastTireDate || undefined,
    },
  };
};

// Function to convert API model to form values - COMPLETE VERSION
export const mapCarModelToForm = (car: ICarModel): any => {
  return {
    // === CAR DEFINITION FIELDS ===
    plate: car.carDefinition?.plate || '',
    branch: car.carDefinition?.branch || '',
    brand: car.carDefinition?.brand || '',
    location: car.carDefinition?.location || '',
    type: car.carDefinition?.modelType || '', // model 'modelType' -> form 'type'
    carStatus: car.carDefinition?.carStatus || 'active',
    vehicleGroup: car.carDefinition?.vehicleGroup || '',
    passiveReason: car.carDefinition?.deactivationReason || '', // model 'deactivationReason' -> form 'passiveReason'
    transmission: car.carDefinition?.transmission || '',
    color: car.carDefinition?.color || '',
    fuelType: car.carDefinition?.fuelType || '',
    lastKm: car.carDefinition?.lastKm || 0,
    model: car.carDefinition?.modelYear || new Date().getFullYear(), // model 'modelYear' -> form 'model'
    lastFuel: car.carDefinition?.lastFuel || '',
    webReservation: car.carDefinition?.webReservationStatus || 'available', // model 'webReservationStatus' -> form 'webReservation'
    officeReservation: car.carDefinition?.officeReservationStatus || 'available', // model 'officeReservationStatus' -> form 'officeReservation'
    
    // === CAR DETAIL FIELDS ===
    vehicleStatus: car.carDetail?.vehicleStatus || 'active',
    ogsHgsAvailable: car.carDetail?.hasOgsHgs || 'no', // model 'hasOgsHgs' -> form 'ogsHgsAvailable'
    bodyType: car.carDetail?.bodyType || '',
    cleaningStatus: car.carDetail?.cleaningStatus || '',
    licenseDate: car.carDetail?.licenseDate || '',
    licenseDocumentNo: car.carDetail?.licenseDocumentNo || '',
    registrationDate: car.carDetail?.registrationDate || '',
    chassisNumber: car.carDetail?.chassisNumber || '',
    engineNumber: car.carDetail?.engineNumber || '',
    insuranceFollow: car.carDetail?.insuranceFollow || '',
    ogsHgsNumber: car.carDetail?.ogsHgsNumber || '',
    ogsHgsBalance: car.carDetail?.ogsHgsBalance || 0,
    radioCode: car.carDetail?.radioCode || '',
    enginePower: car.carDetail?.enginePower || '',
    cylinderVolume: car.carDetail?.cylinderVolume || 0,
    integrationCode: car.carDetail?.integrationCode || '',
    autoEngineStop: car.carDetail?.autoEngineStop || 'no',
    hgsCompany: car.carDetail?.hgsCompany || '',
    vehicleTrackingNumber: car.carDetail?.vehicleTrackingNumber || '',
    periodicServiceTracking: car.carDetail?.periodicServiceTracking || 'no',
    detail: car.carDetail?.detail || '',

    // === CAR INSURANCE FIELDS ===
    trafficInsuranceStartDate: car.carInsurance?.trafficInsuranceStartDate || '',
    trafficInsuranceEndDate: car.carInsurance?.trafficInsuranceEndDate || '',
    insuranceCompany: car.carInsurance?.insuranceCompany || '',
    insuranceAgency: car.carInsurance?.insuranceAgency || '',
    trafficPolicyNumber: car.carInsurance?.trafficPolicyNumber || '',
    inspectionStartDate: car.carInsurance?.inspectionStartDate || '',
    inspectionEndDate: car.carInsurance?.inspectionEndDate || '',
    cascoStartDate: car.carInsurance?.cascoStartDate || '',
    cascoEndDate: car.carInsurance?.cascoEndDate || '',
    cascoCompany: car.carInsurance?.cascoCompany || '',
    cascoAgency: car.carInsurance?.cascoAgency || '',
    cascoPolicyNumber: car.carInsurance?.cascoPolicyNumber || '',
    cascoCode: car.carInsurance?.cascoCode || '',

    // === CAR WARNING/FINANCIAL FIELDS ===
    fleetEntryDate: car.carWarning?.fleetEntryDate || '',
    cascoValue: car.carWarning?.cascoValue || 0,
    purchasingCompany: car.carWarning?.purchasingCompany || '',
    closeDate: car.carWarning?.closeDate || '',
    pledgeStatus: car.carWarning?.pledgeStatus || '',
    vehicleOwner: car.carWarning?.vehicleOwner || '',
    vehicleOwnerCompany: car.carWarning?.vehicleOwnerCompany || '',
    vehicleOwnerNumber: car.carWarning?.vehicleOwnerNumber || '',
    secondaryVehicleOwner: car.carWarning?.secondaryVehicleOwner || '',
    purchaseTotalPrice: car.carWarning?.purchaseTotalPrice || 0,
    purchasePriceExclTax: car.carWarning?.purchasePriceExclTax || 0,
    purchaseVAT: car.carWarning?.purchaseVAT || 0,
    purchaseSCT: car.carWarning?.purchaseSCT || 0,
    purchaseDate: car.carWarning?.purchaseDate || '',
    purchaseInvoiceNumber: car.carWarning?.purchaseInvoiceNumber || '',
    vehicleSecondHandPrice: car.carWarning?.vehicleSecondHandPrice || 0,
    fleetManagementCost: car.carWarning?.fleetManagementCost || 0,
    monthlyCost: car.carWarning?.monthlyCost || 0,
    additionalPrice: car.carWarning?.additionalPrice || 0,
    creditCompany: car.carWarning?.creditCompany || '',
    externalSupplierKmLimit: car.carWarning?.externalSupplierKmLimit || 0,
    assistServiceProvider: car.carWarning?.assistServiceProvider || '',
    specialCode1: car.carWarning?.specialCode1 || '',
    specialCode2: car.carWarning?.specialCode2 || '',
    specialCode3: car.carWarning?.specialCode3 || '',
    specialCode4: car.carWarning?.specialCode4 || '',
    specialCode5: car.carWarning?.specialCode5 || '',
    fleetExitDate: car.carWarning?.fleetExitDate || '',
    plannedExitDate: car.carWarning?.plannedExitDate || '',
    saleWarningKm: car.carWarning?.saleWarningKm || 0,
    description: car.carWarning?.description || '',
    tireInfo: car.carWarning?.tireInfo || '',
    snowTireStatus: car.carWarning?.snowTireStatus || 'YOK',
    spareKeyStatus: car.carWarning?.spareKeyStatus || 'YOK',
    tireWarningKm: car.carWarning?.tireWarningKm || 0,
    lastMaintenanceNumber: car.carWarning?.lastMaintenanceNumber || '',
    lastMaintenanceKm: car.carWarning?.lastMaintenanceKm || 0,
    lastMaintenanceDate: car.carWarning?.lastMaintenanceDate || undefined,
    kmInspectionNumber: car.carWarning?.kmInspectionNumber || '',
    inspectionType: car.carWarning?.inspectionType || '',
    kmInspectionDate: car.carWarning?.kmInspectionDate || undefined,
    lastPeriodicNumber: car.carWarning?.lastPeriodicNumber || '',
    lastPeriodicKm: car.carWarning?.lastPeriodicKm || 0,
    lastPeriodicDate: car.carWarning?.lastPeriodicDate || undefined,
    lastTireNumber: car.carWarning?.lastTireNumber || '',
    lastTireKm: car.carWarning?.lastTireKm || 0,
    lastTireDate: car.carWarning?.lastTireDate || undefined,
  };
}; 